/* Copyright (c) 2023 MOCA Systems, Inc.  All rights reserved. */

import { Router } from '@angular/router';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { Observable, merge, throwError, defer } from 'rxjs';
import { exhaustMap, filter, last, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';

import {
	PROCORE_SUCCESSFULLY_LOGGED_IN,
	PROCORE_REDIRECTING_TO_PROCORE_SITE,
	PROCORE_LOGIN_IS_REQUIRED,
	PROCORE_TOKEN_REFRESHED,
	PROCORE_UNEXPECTED_ERROR,
} from '@touchplan/strings';

import { ProcoreService } from './procore.service';
import { RouteDataStateService } from './route-data.state.service';
import { LoggerServiceFactory } from '../common/logging/LoggerServiceFactory';
import { WindowLocationService } from './window-location.service';

export enum ProcoreLoginStatus {
	NotInitialized = 'NotInitialized',
	Error = 'Error',
	Authenticated = 'Authenticated',
}

interface IProcoreLoginState {
	status: ProcoreLoginStatus;
	token: string;
	projectId: string;
	procoreProjectId: string;
	userId: string;
}

const INITIAL_STATE: IProcoreLoginState = {
	status: ProcoreLoginStatus.NotInitialized,
	token: null,
	userId: null,
	procoreProjectId: null,
	projectId: null,
};

export interface ITouchplanSessionCookieData {
	userId: string;
	projectId: string;
	procoreProjectId: string;
	redirect: string;
	token: ITouchplanSessionCookieDataToken;
}

export interface ITouchplanSessionCookieDataToken {
	access_token: string;
	token_type: string;
	expires_in: number;
	refresh_token: string;
	created_at: number;
}
/**
 * This service provides the state of Procore integration authentication.
 * Also it contains the side effects to authenticate and sign in the user against Procore.
 * Finally this service implements a Component Store pattern to handle state management. If you want more information
 * about you can follow the link below:
 * https://medium.com/ngconf/using-ngrx-component-store-introduction-7787ce250edc
 */
@Injectable({
	providedIn: 'root',
})
export class ProcoreLoginStateService extends ComponentStore<IProcoreLoginState> {
	private logger = LoggerServiceFactory.create('ProcoreLoginService');
	/**
	 * They provide a state slice.
	 * A selector creates a new multicast observable of a slice of the service state data that is is emited only on changes
	 * also it repeats its last emited value on new subscriptions and it's tied to service's Angular livecycle
	 * https://github.com/ngrx/platform/blob/4afac00b887281a6562aa565127352c0b165a4a2/modules/component-store/src/component-store.ts#L276
	 * Framework's oficial explanation 👇
	 * https://ngrx.io/guide/component-store/read
	 */
	public token$: Observable<string> = this.select((state) => state.token);
	public projectId$: Observable<string> = this.select((state) => state.projectId);
	public userId$: Observable<string> = this.select((state) => state.userId);
	public authenticationStatus$: Observable<ProcoreLoginStatus> = this.select((state) => state.status);

	constructor(
		private router: Router,
		private cookieService: CookieService,
		private procoreService: ProcoreService,
		private afAuth: AngularFireAuth,
		private routeDataState: RouteDataStateService,
		private windowLocationService: WindowLocationService
	) {
		// We set the initial state which under the hook we can say that it is a just a behavior subject with provided model.
		// https://github.com/ngrx/platform/blob/4afac00b887281a6562aa565127352c0b165a4a2/modules/component-store/src/component-store.ts#L63
		super(INITIAL_STATE);

		// Listen changes on user or projectId in order to clean state if user or project changed when current user is already authorizated
		this.resetStateOnUserOrProjectChanges();
	}

	/**
	 * Updaters can update part or full state.
	 * Also they are just very fancy "next()"s for the service state "behavior subject" that also provide the current state.
	 * Finally they can accept an observable as input
	 * https://github.com/ngrx/platform/blob/4afac00b887281a6562aa565127352c0b165a4a2/modules/component-store/src/component-store.ts#L133
	 * Framework's oficial explanation 👇
	 * https://ngrx.io/guide/component-store/write
	 */

	private setUserAuthenticatedState = this.updater(
		(
			_,
			{
				userId,
				token,
				projectId,
				procoreProjectId,
			}: {
				userId: string;
				token: string;
				projectId: string;
				procoreProjectId: string;
			}
		) => ({
			userId,
			token,
			projectId,
			procoreProjectId,
			status: ProcoreLoginStatus.Authenticated,
		})
	);

	private resetState = this.updater((_) => ({
		status: ProcoreLoginStatus.NotInitialized,
		token: null,
		projectId: null,
		userId: null,
		procoreProjectId: null,
	}));

	private setErrorState = this.updater((state) => ({
		...state,
		token: null,
		status: ProcoreLoginStatus.Error,
	}));

	/**
	 * They mostly are for handle side effects that mutate the service state.
	 * They usually are called from outside the service.
	 * Effects are just very fancy subscriptions that are tied to the angular's lifecycle hook to being destroyed when the service is destroyed.
	 * https://github.com/ngrx/platform/blob/4afac00b887281a6562aa565127352c0b165a4a2/modules/component-store/src/component-store.ts#L315
	 * Framework's oficial explanation 👇
	 * https://ngrx.io/guide/component-store/effect
	 *
	 * completeAuthentication method allows user to complete their authentication once they are redirected back from Procore.
	 * It also takes the information stored in the cookie to redirect the user to a given project.
	 * It accepts one parameter with the code provide by Procore redirect URL
	 */
	public completeAuthentication = this.effect<string>((authorizationCode$) => {
		return authorizationCode$.pipe(
			switchMap((code) =>
				this.procoreService.loginWithAccessCode(code).pipe(
					last(),
					withLatestFrom(this.getCurrentUserId$()),
					/**
					 * Tap response is a short hand for tap(), catchError() combined operators which used together prevent mostly disconnect the subscription on server errors
					 * Framework's oficial explanation 👇
					 * https://ngrx.io/guide/component-store/effect#tapresponse
					 */
					tapResponse(
						([unparsedToken, userId]) => {
							const cookieData = this.getTouchplanSessionCookieData();
							const token = this.parseTokenFromResponse(unparsedToken);
							if (token && cookieData?.projectId && cookieData?.procoreProjectId) {
								this.setUserAuthenticatedState({
									procoreProjectId: cookieData.procoreProjectId,
									projectId: cookieData.projectId,
									token,
									userId,
								});
								Logging.notice(PROCORE_SUCCESSFULLY_LOGGED_IN);
								this.router.navigate(['projects', cookieData.projectId]);
							} else {
								// if request was ok but there is not token in there or cookie can't be parsed
								// it will be handled as an error state
								throw new Error('Unexpected authentication response');
							}
						},
						(error: HttpErrorResponse) => this.handleError(error)
					)
				)
			)
		);
	});

	/**
	 * authenticate method initialize the authentication against Procore
	 * it also directs the user to Procore if not authenticated yet
	 * it accepts a configuratio  paramter with the following opions:
	 * allowRedirect: boolean = allows to perfom a redirect when authentication against Procore is required
	 * silentMode: boolean = when it is true it doesn't show any message.
	 */
	public authenticate = this.effect<{
		allowRedirect: boolean;
		silentMode: boolean;
	}>((configuration$) => {
		return configuration$.pipe(
			withLatestFrom(this.getCurrentUserId$(), this.routeDataState.projectData$),
			switchMap(([{ allowRedirect, silentMode }, userId, { projectId, procoreProjectId }]) =>
				defer(() => {
					if (!userId || !projectId || !procoreProjectId) {
						return throwError('user, project id or Procore project id are required');
					}
					return this.procoreService.authenticate(projectId, userId, procoreProjectId).pipe(last());
				}).pipe(
					tapResponse(
						(response) => {
							// handle redirect URL response
							if (response.startsWith('http')) {
								this.handleRedirectURLResponse(response, allowRedirect);
							} else if (this.parseTokenFromResponse(response)) {
								// handle token response
								this.handleTokenResponse(
									this.parseTokenFromResponse(response),
									silentMode,
									userId,
									projectId,
									procoreProjectId
								);
							} else {
								throw new Error('Unexpected authentication response');
							}
						},
						(error: HttpErrorResponse) => this.handleError(error)
					)
				)
			)
		);
	});

	private resetStateOnUserOrProjectChanges = this.effect((obs$) => {
		return obs$.pipe(
			// exhaustMap is required to ignore aditional subscriptions of this side effect
			exhaustMap(() =>
				merge(
					this.getCurrentUserId$().pipe(
						withLatestFrom(this.userId$, this.authenticationStatus$),
						filter(
							([userId, currentUserId, authenticationStatus]) =>
								!!userId &&
								userId !== currentUserId &&
								authenticationStatus === ProcoreLoginStatus.Authenticated
						)
					),
					this.routeDataState.projectData$.pipe(
						withLatestFrom(this.projectId$, this.authenticationStatus$),
						filter(
							([routeProjectData, projectId, authenticationStatus]) =>
								!!routeProjectData?.projectId &&
								projectId !== routeProjectData.projectId &&
								authenticationStatus === ProcoreLoginStatus.Authenticated
						)
					)
				).pipe(tap(() => this.resetState()))
			)
		);
	});

	private getCurrentUserId$(): Observable<string> {
		return this.afAuth.user.pipe(map((user) => (user ? user.uid : null)));
	}

	private handleRedirectURLResponse(redirectURL: string, allowRedirect: boolean) {
		//Redirect the user to Procore site.
		if (allowRedirect) {
			this.windowLocationService.redirect(redirectURL);
			Logging.warning(PROCORE_REDIRECTING_TO_PROCORE_SITE);
		} else {
			Logging.warning(PROCORE_LOGIN_IS_REQUIRED);
		}
	}

	private handleTokenResponse(
		token: string,
		silentMode: boolean,
		userId: string,
		projectId: string,
		procoreProjectId: string
	) {
		this.setUserAuthenticatedState({
			token,
			userId,
			projectId,
			procoreProjectId,
		});
		if (silentMode) {
			this.logger.warn(PROCORE_TOKEN_REFRESHED);
		} else {
			Logging.notice(PROCORE_SUCCESSFULLY_LOGGED_IN);
		}
	}

	private getTouchplanSessionCookieData(): ITouchplanSessionCookieData {
		const touchplanSesionCookie = this.cookieService.get('touchplanSession').replace(/['"]+/g, '');
		try {
			// Convert cookie to text from base64 and parse the JSON
			return JSON.parse(atob(touchplanSesionCookie));
		} catch (err) {
			this.logger.warn('error getting cookie', err);
			return null;
		}
	}

	private parseTokenFromResponse(authenticationResponse: string): string {
		try {
			// Parse text response to JSON an returns the token property
			if (authenticationResponse.includes('token')) {
				return JSON.parse(authenticationResponse).token;
			}
			return null;
		} catch (err) {
			this.logger.warn('error parsing token', err);
			return null;
		}
	}

	private handleError(error?: HttpErrorResponse, message?: string) {
		this.setErrorState();
		if (error?.error instanceof ErrorEvent) {
			// A client-side or network error occurred...
			Logging.warning(message || error.error.message);
			this.logger.warn(message || error.error.message);
		} else {
			if (message) {
				this.logger.error(message);
			} else if (error) {
				this.logger.error(error);
			}
			// Unexpected error.
			Logging.warning(PROCORE_UNEXPECTED_ERROR);
		}
	}
}
